<template>
  <div>
		<NavBar v-if="admin" :admin="true" :items="items" />
		<Breadcrumbs v-else :items="items" />
    <v-main :class="admin ? 'mx-12' : 'pa-0'">
			<v-container>
        <v-row no-gutters class="pt-4 px-4">
				<v-col cols="12" md="12">
					<v-text-field
						solo
						v-model="search"
						:placeholder="$gettext('Search')"
						outlined
						prepend-inner-icon="search"
						hide-details
					></v-text-field>
				</v-col>
				<v-col cols="12" md="12">
					<v-row no-gutters class="my-3">
						<span class="primary--text">
							<b v-translate>Type</b>
						</span>
						<v-btn-toggle class="ml-2" v-model="auricular" mandatory @change="group = 'all'; getPoints()">
							<v-btn :value="false" class="text-overline" small>
								<translate>Body</translate>
							</v-btn>
							<v-btn :value="true" class="text-overline" small>
								<translate>Auricular</translate>
							</v-btn>
						</v-btn-toggle>
						<v-spacer></v-spacer>
						<v-btn color="primary" class="text-overline mr-1" key="download" small @click="downloadExel">
							<v-icon small class="mr-1">mdi-download</v-icon>
							Descarregar Exel
						</v-btn>
						<v-btn color="primary" class="text-overline" key="upload" small @click="showUploadExel = true; uploadError = false" :loading="uploadingExel">
							<v-icon small class="mr-1">mdi-upload</v-icon>
							Carregar Exel
						</v-btn>
						<v-dialog v-model="showUploadExel" max-width="35%" scrollable>
							<v-card>
								<v-card-title class="title primary--text mb-2 secondary">
									Carregar Exel (XLSX)
								</v-card-title>
								<v-card-text>
									<VueFileAgent
										ref="vueFileAgent"
										:theme="'list'"
										:multiple="false"
										:deletable="true"
										:meta="true"
										:accept="'.xlsx'"
										:maxSize="'50MB'"
										:helpText="$gettext('Drag or click here to upload documents')"
										:errorText="{
											type: 'Invalid file type. Only XLSX are allowed',
											size: 'Files cannot exceed 50MB in size'
										}"
										@beforedelete="exelFile = null"
										v-model="exelFile"
									></VueFileAgent>
								</v-card-text>
								<v-divider></v-divider>
								<v-card-actions>
									<v-alert dense v-model="uploadError" color="red lighten-2" class="text-caption ma-0" icon="warning">
										<translate>Error</translate>
									</v-alert>
									<v-spacer></v-spacer>
									<v-btn color="primary darken-1" text @click="uploadExel" :loading="uploadingExel">
										<translate>Save</translate>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-dialog>
					</v-row>
				</v-col>
				<v-col cols="12" md="12">
					<v-row no-gutters>
						<span class="primary--text">
							<b v-translate>Groups</b>
						</span>
						<v-btn-toggle style="display: block" class="ml-2" v-model="group" mandatory @change="getPoints()">
							<v-btn value="all" class="text-overline" small>
								<translate>All</translate>
							</v-btn>
							<v-btn v-for="g in groups" :value="g.id" class="text-overline" small :key="g.id">
								{{g.name}}
							</v-btn>
						</v-btn-toggle>
					</v-row>
				</v-col>
				<v-col cols="12" md="12" class="mt-3">
					<v-progress-linear
						:active="loading"
						:indeterminate="loading"
						top
						color="primary"
					></v-progress-linear>
					<template v-if="!loading">
            <p v-if="points.length == 0" class="font-weight-thin ml-2" v-translate>No results.</p>
						<AcuPointList :list="points" :settings="true" :infiniteScroll="true" />
					</template>
				</v-col>
			</v-row>
			</v-container>
    </v-main>
  </div>
</template>

<script>
import NavBar from "@/components/authentication/NavBar.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import AcuPointList from "@/components/acupuncture/AcuPointList.vue";
import axios from "axios";

export default {
	name: "AcupuncturePoints",
	props: ["admin"],
  components: {
		NavBar,
		Breadcrumbs,
    AcuPointList
  },
  data: () => ({
    auricular: false,
    search: "",
    group: 'all',
		showUploadExel: false,
		uploadingExel: false,
		uploadError: false,
		exelFile: null
  }),
  computed: {
		items() {
			if(this.admin) {
				return [
					{
						text: this.$gettext("Dashboard"),
						disabled: false,
						link: true,
						to: "/admin/dashboard"
					},
					{
						text: this.$gettext("Acupuncture"),
						disabled: false,
						link: true,
						to: {name: 'admin-acu'}
					},
					{
						text: this.$gettext("Points"),
						disabled: true,
						link: true
					}
				];
			} else {
				return [
					{
						text: this.$gettext("Settings"),
						disabled: true,
						link: true
					},
					{
						text: this.$gettext("Treatments"),
						disabled: false,
						link: true,
						to: { name: "settings", params: { tab: "treatments" } }
					},
					{
						text: this.$gettext("Acupuncture"),
						disabled: false,
						link: true,
						to: {name: 'acupuncture'}
					},
					{
						text: this.$gettext("Points"),
						disabled: true,
						link: true
					}
				];
			}
    },
    points() {
			return this.$store.getters.acuSearchedPoints; 
		},
		groups() {
			return this.$store.getters.acuClassicGroups(this.auricular);
		},
		loading() {
      return this.$store.getters.acuLoading;
		}
  },
  methods: {
    makeRequest() {
			clearTimeout(this._timerId);
			if (this.search != null) {
				this._timerId = setTimeout(() => {
          this.getPoints();
				}, 500);
			}
    },
    getPoints() {
      this.$store.dispatch("SEARCH_POINTS", {
        value: this.search.toLowerCase(),
        auricular: this.auricular,
        group: this.group
      }).then(() => {});
    },
		downloadExel() {
			var link = document.createElement("a");
			link.href = "https://api.naltio.com/acupuncture/points/exel?auricular=" + this.auricular;
			//link.href = "http://127.0.0.1:5500/acupuncture/points/exel?auricular=" + this.auricular;
			link.target = "_self";
			document.body.appendChild(link);
      link.click();
		},
		uploadExel() {
			if(this.exelFile) {
				this.uploadingExel = true;
				this.uploadError = false;
				const formData = new FormData();
				formData.append("acu_points_exel", this.exelFile.file);
				axios
					.post("/acupuncture/points/upload-exel", formData)
					.then(response => {
						console.log(response.data);
						this.uploadingExel = false;
						if(response.data.result == 'error') {
							this.uploadError = true;
						} else {
							this.exelFile = null;
							this.showUploadExel = false;
						}
					})
					.catch(error => {
						console.log(error);
						this.uploadingExel = false;
						this.uploadError = true;
					});
			}
		}
  },
  watch: {
    search() {
			//if (!value) {
      //	return;
			//}
			this.makeRequest();
		}
  },
  created() {
    this.$store.dispatch("GET_DATA").then(() => {
      this.getPoints();
		});
	}
}
</script>